// ─── Parser SSE pur (story 5.5) ──────────────────────────────────────────────
// Consomme des fragments de texte arbitraires (frontières de chunk réseau),
// découpe les événements sur une ligne vide (`\n\n`, CRLF toléré), lit les
// lignes `data:` et dispatche des `ChatSSEEvent` typés. `[DONE]` = fin.
// Robustesse : JSON malformé ignoré (warn en dev), `type` inconnu ou champs
// invalides ignorés (compatibilité ascendante avec de futurs événements backend).

import { CHAT_SSE_EVENT_TYPES, type ChatSSEEvent } from './chatTypes'

export type SSEParser = {
  push: (textChunk: string) => void
  flush: () => void
}

const DONE_SENTINEL = '[DONE]'

function warnDev(message: string, detail?: unknown): void {
  const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env
  if (env?.DEV) console.warn(`[sseParser] ${message}`, detail)
}

function isString(v: unknown): v is string {
  return typeof v === 'string'
}

// `toolCallId` (story 6.2) : absent accepté, présent → doit être une string.
function isOptionalString(v: unknown): boolean {
  return v === undefined || isString(v)
}

// Validation par type : un événement connu mais aux champs invalides est
// ignoré comme un type inconnu (jamais de crash du reducer en aval).
function isKnownEvent(value: unknown): value is ChatSSEEvent {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  const type = v.type
  if (!isString(type) || !(CHAT_SSE_EVENT_TYPES as readonly string[]).includes(type)) return false
  switch (type) {
    case 'content':
      return isString(v.content)
    case 'tool_call':
      return isString(v.name) && 'arguments' in v && isOptionalString(v.toolCallId)
    case 'tool_result':
      return isString(v.name) && 'result' in v && isOptionalString(v.toolCallId)
    case 'action_card':
      return (
        (v.tier === 2 || v.tier === 3) &&
        isString(v.title) &&
        isString(v.description) &&
        isString(v.name)
      )
    case 'suggestion_pills':
      return Array.isArray(v.suggestions) && v.suggestions.every(isString)
    case 'error':
      return isString(v.error)
    default:
      return false
  }
}

export function createSSEParser(
  onEvent: (event: ChatSSEEvent) => void,
  onDone: () => void
): SSEParser {
  let buffer = ''
  let pendingCR = ''
  let done = false

  const handleBlock = (block: string): void => {
    if (done) return
    const dataLines: string[] = []
    for (const line of block.split('\n')) {
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''))
      }
      // Les autres champs SSE (event:, id:, retry:, commentaires `:`) sont ignorés.
    }
    if (dataLines.length === 0) return
    const data = dataLines.join('\n')
    if (data.trim() === DONE_SENTINEL) {
      done = true
      onDone()
      return
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(data)
    } catch (e: unknown) {
      warnDev('événement JSON malformé ignoré', e)
      return
    }
    if (!isKnownEvent(parsed)) {
      warnDev('événement inconnu ou invalide ignoré', parsed)
      return
    }
    onEvent(parsed)
  }

  const drain = (): void => {
    let idx = buffer.indexOf('\n\n')
    while (idx !== -1) {
      const block = buffer.slice(0, idx)
      buffer = buffer.slice(idx + 2)
      handleBlock(block)
      idx = buffer.indexOf('\n\n')
    }
  }

  return {
    push(textChunk: string) {
      if (done) return
      // Un `\r` final peut être la 1re moitié d'un CRLF coupé entre deux
      // chunks : on le garde en attente jusqu'au chunk suivant.
      let text = pendingCR + textChunk
      pendingCR = ''
      if (text.endsWith('\r')) {
        pendingCR = '\r'
        text = text.slice(0, -1)
      }
      buffer += text.replace(/\r\n?/g, '\n')
      drain()
    },
    flush() {
      if (done) return
      if (pendingCR) {
        buffer += '\n'
        pendingCR = ''
      }
      drain()
      if (buffer.trim().length > 0) {
        const rest = buffer
        buffer = ''
        handleBlock(rest)
      }
      buffer = ''
    },
  }
}
